/**
 * Révisions espacées (PDF §1 « répétition régulière », §13 « À revoir aujourd'hui »).
 *
 * La planification vit dans `progress/$chapterId/review` (voir progressService :
 * scheduleFirstReview / nextReviewState). Ce module choisit les chapitres dus, compose
 * une courte série d'exercices et enregistre le résultat (XP via applyActivity : même
 * mécanique de série quotidienne que les exercices et les tests, sans duplication).
 */
import { ref, get, set } from "firebase/database";
import { db } from "../firebase";
import type { Catalog, ChapterMeta, Exercise, ExerciseLevel, FullChapter, Year } from "../content/types";
import {
  ChapterProgress,
  ProgressMap,
  REVIEW_PASS_SCORE,
  XP,
  applyActivity,
  nextReviewState,
  refreshParentSummary,
} from "./progressService";
import { chaptersForYear } from "../utils/progressStats";
import { track } from "./analytics";

export const REVIEW_EXERCISE_COUNT = 4;
export const MAX_DUE_REVIEWS = 3;

export interface DueReview {
  chapter: ChapterMeta;
  /** Échéance (ms). */
  dueAt: number;
  /** Jours de retard entiers (0 = due aujourd'hui). */
  overdueDays: number;
}

/** Chapitres dont la révision est due (nextDueAt ≤ maintenant), du plus en retard au moins en retard, max 3. */
export function dueReviews(catalog: Catalog, progress: ProgressMap, year: Year, now = Date.now()): DueReview[] {
  return chaptersForYear(catalog, year)
    .filter((c) => {
      const due = progress[c.id]?.review?.nextDueAt;
      return typeof due === "number" && due <= now;
    })
    .map((c) => {
      const dueAt = progress[c.id].review!.nextDueAt;
      return { chapter: c, dueAt, overdueDays: Math.max(0, Math.floor((now - dueAt) / (24 * 60 * 60 * 1000))) };
    })
    .sort((a, b) => a.dueAt - b.dueAt)
    .slice(0, MAX_DUE_REVIEWS);
}

/** Nombre de révisions qui seront dues à un instant donné (pour le rappel du matin). */
export function countReviewsDueAt(catalog: Catalog, progress: ProgressMap, year: Year, at: number): number {
  return chaptersForYear(catalog, year).filter((c) => {
    const due = progress[c.id]?.review?.nextDueAt;
    return typeof due === "number" && due <= at;
  }).length;
}

function shuffle<T>(list: T[]): T[] {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * 4 exercices pour la révision : priorité aux exercices ratés au dernier test, puis à ceux
 * jamais réussis, puis aux autres ; on varie les niveaux (un par niveau tant que possible).
 */
export function pickReviewExercises(chapter: FullChapter, p: ChapterProgress | undefined, count = REVIEW_EXERCISE_COUNT): Exercise[] {
  const tests = Object.values(p?.tests || {}).sort((a, b) => b.at - a.at);
  const lastWrong = new Set(tests[0]?.wrongIds || []);
  const priority = (e: Exercise) => (lastWrong.has(e.id) ? 0 : p?.exercises?.[e.id]?.correct ? 2 : 1);
  const ranked = shuffle(chapter.exercises).sort((a, b) => priority(a) - priority(b));

  const chosen: Exercise[] = [];
  const levels = new Set<ExerciseLevel>();
  // Premier passage : un exercice par niveau, dans l'ordre de priorité.
  for (const e of ranked) {
    if (chosen.length >= count) break;
    if (levels.has(e.level)) continue;
    chosen.push(e);
    levels.add(e.level);
  }
  // Complément : les suivants par priorité, sans doublon.
  for (const e of ranked) {
    if (chosen.length >= count) break;
    if (!chosen.includes(e)) chosen.push(e);
  }
  return shuffle(chosen).sort((a, b) => a.level - b.level);
}

export interface ReviewOutcome {
  passed: boolean;
  xpGained: number;
  nextDueAt: number;
  streakExtended: boolean;
  levelUp: boolean;
}

/** Enregistre une révision (score 0-100) : replanifie, +XP (15 réussie / 5 sinon), résumé parent. */
export async function recordReview(uid: string, chapter: FullChapter, score: number): Promise<ReviewOutcome> {
  const passed = score >= REVIEW_PASS_SCORE;
  const pRef = ref(db, `users/${uid}/progress/${chapter.id}`);
  const snap = await get(pRef);
  const p: ChapterProgress = snap.val() || { completion: 0 };
  const review = nextReviewState(p.review, passed);
  await set(pRef, { ...p, review, lastActivityAt: Date.now() });
  track("review_completed", { score, passed });

  const xpGained = passed ? XP.reviewPassed : XP.reviewFailed;
  const res = await applyActivity(uid, xpGained);
  refreshParentSummary(uid);
  return { passed, xpGained, nextDueAt: review.nextDueAt, streakExtended: res.streakExtended, levelUp: res.levelUp };
}
